/**
 * Hook React para detección de dispositivos USB monitorizados.
 *
 * Solo activo en entorno Electron. En navegador devuelve el estado vacío
 * sin errores.
 */

import { useState, useEffect, useCallback } from 'react';
import { useIsElectron } from './useElectron';
import type { UsbDeviceInfo, UsbDeviceType, UsbDeviceEvent, UsbDeviceDebugInfo } from '../types/electron/usb-device.types';

export interface UsbDeviceLogEntry {
  type: 'connected' | 'disconnected';
  deviceName: string;
  timestamp: number;
}

interface UseUsbDeviceReturn {
  /** Lista de dispositivos monitorizados actualmente conectados */
  connectedDevices: UsbDeviceInfo[];
  /** True si las Magic Leap 2 están conectadas */
  isMagicLeapConnected: boolean;
  /** True si hay un ratón 3D conectado por USB a este equipo */
  isSpaceMouseConnected: boolean;
  /**
   * True si el proceso principal puede consultar los dispositivos al sistema
   * operativo. Cuando lo es, su respuesta es la fuente de verdad sobre qué hay
   * conectado; cuando no, hay que recurrir a señales indirectas.
   */
  isSystemDetectionSupported: boolean;
  /** True si se está en Electron y el sistema está activo */
  isSupported: boolean;
  /** Helper: indica si un tipo de dispositivo concreto está conectado */
  isDeviceConnected: (type: UsbDeviceType) => boolean;
  /** Log de eventos USB (últimos 20) */
  eventLog: UsbDeviceLogEntry[];
  /** Solicita la info de debug completa */
  getDebugInfo: () => Promise<UsbDeviceDebugInfo | null>;
}

export function useUsbDevice(): UseUsbDeviceReturn {
  const isElectron = useIsElectron();

  const [connectedDevices, setConnectedDevices] = useState<UsbDeviceInfo[]>([]);
  const [eventLog, setEventLog] = useState<UsbDeviceLogEntry[]>([]);
  const [isSystemDetectionSupported, setIsSystemDetectionSupported] = useState(false);

  useEffect(() => {
    if (!isElectron || !window.electron?.usbDevice) return;

    const api = window.electron.usbDevice;

    // Inicializar WebUSB monitoring (activa session events en main + WebUSB events)
    api.initMonitoring().catch(console.error);

    // Obtener estado inicial
    api.getConnected().then(setConnectedDevices).catch(console.error);

    // Saber si el proceso principal puede preguntarle al sistema operativo
    api.getDebugInfo()
      .then((info: UsbDeviceDebugInfo | null) => setIsSystemDetectionSupported(!!info?.systemDetection?.supported))
      .catch(() => setIsSystemDetectionSupported(false));

    // Suscribirse a cambios en tiempo real
    const unsubscribe = api.onChange((event: UsbDeviceEvent) => {
      setConnectedDevices((prev) => {
        if (event.type === 'connected') {
          const alreadyPresent = prev.some(
            (d) => d.vendorId === event.device.vendorId && d.productId === event.device.productId,
          );
          return alreadyPresent ? prev : [...prev, event.device];
        } else {
          return prev.filter(
            (d) => !(d.vendorId === event.device.vendorId && d.productId === event.device.productId),
          );
        }
      });

      // Log de eventos (máximo 20)
      setEventLog((prev) => [
        { type: event.type, deviceName: event.device.productName, timestamp: event.timestamp },
        ...prev,
      ].slice(0, 20));
    });

    return unsubscribe;
  }, [isElectron]);

  const isDeviceConnected = (type: UsbDeviceType): boolean =>
    connectedDevices.some((d) => d.deviceType === type);

  const getDebugInfo = useCallback(async (): Promise<UsbDeviceDebugInfo | null> => {
    if (!window.electron?.usbDevice) return null;
    try {
      return await window.electron.usbDevice.getDebugInfo();
    } catch {
      return null;
    }
  }, []);

  return {
    connectedDevices,
    isMagicLeapConnected: isDeviceConnected('magic-leap-2'),
    isSpaceMouseConnected: isDeviceConnected('spacemouse'),
    isSystemDetectionSupported,
    isSupported: isElectron && !!window.electron?.usbDevice,
    isDeviceConnected,
    eventLog,
    getDebugInfo,
  };
}
